/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {TvpVehicleProgram} from '../../../../models/tvp-vehicle-program.model';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-select-vehicle-program-pop-up',
    templateUrl: './select-vehicle-program-pop-up.component.html',
    styleUrls: ['./select-vehicle-program-pop-up.component.scss'],
    standalone: false
})
export class SelectVehicleProgramPopUpComponent implements OnInit {
  showSpinner: boolean;

  columnHeaders: ColDef[] = [
    {field: 'mktDiv', headerName: 'Market Division', filter: AutocompleteFilterComponent},
    {field: 'brand', headerName: 'Brand', filter: AutocompleteFilterComponent},
    {field: 'prgmCode', headerName: 'Program Code', filter: AutocompleteFilterComponent},
    {field: 'styleCode', headerName: 'Style Code', filter: AutocompleteFilterComponent},
    {field: 'bodyStyle', headerName: 'Body Style', filter: AutocompleteFilterComponent},
  ];

  vehScenId: number;

  architecture: string;

  vehiclePrograms: TvpVehicleProgram[] = [];
  selectedPrograms: TvpVehicleProgram[] = [];


  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false,
    isRowSelectable: node => node.data.productLocation !== 'S'
  };

  getRowStyle = (params: any) => {
    if (params.data.productLocation === 'S') {
      return {'background-color': '#2196F311'};
    }
  }

  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: any,
              public dialogRef: MatDialogRef<SelectVehicleProgramPopUpComponent>,
              readonly vehicleScenarioService: VehicleScenarioService,
              public snackBar: FabMessageService) {
    if (data) {
      this.architecture = data.architecture;
      if (data.vehScenId) {
        this.vehScenId = data.vehScenId;
        this.columnHeaders.push({field: 'productLocation', headerName: 'S/P', filter: AutocompleteFilterComponent});
      }
    }
  }

  ngOnInit(): void {
    this.showSpinner = false;
    if (this.vehScenId) {
      this.getVehicleProgramsByVehScenIdAndArch(this.vehScenId, this.architecture);
    } else {
      this.getVehiclePrograms(this.architecture);
    }
  }

  /**
   * gets the vehicle programs to display based on the architecture selected
   * @param architecture the architecture to get the programs by
   */
  getVehiclePrograms(architecture: string): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getTvpVehiclePrograms(architecture).subscribe(
      data => {
        this.vehiclePrograms = data;
      }, error => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        this.showSpinner = false;
      }
    );
  }

  /**
   * Get list of TVP vehicle programs by architecture and vehicle scenario id that is in the scenario
   * and in the vehicle portfolio
   * @param vehScenId id of the vehicle scenario
   * @param architecture the architecture
   */
  getVehicleProgramsByVehScenIdAndArch(vehScenId: number, architecture: string): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getTVPVehicleProgramsByArchAndVehScenId(vehScenId, architecture).subscribe(
      data => {
        this.vehiclePrograms = data;
      }, error => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        this.showSpinner = false;
        // Pre-select the programs that are in the scenario
        this.selectedPrograms = this.vehiclePrograms.filter(program => program.productLocation === 'S');
      }
    );
  }

  /**
   * adds or removes a row from the selected program list
   * @param event the event with the row to be either added or removed
   */
  toggleSelection(event: any): void {
    // You can only toggle selection on records that are in the portfolio
    if (this.selectedPrograms.includes(event.data)) {
      this.selectedPrograms = this.selectedPrograms.filter(entry => {
          return entry !== event.data;
        }
      );
    } else {
      this.selectedPrograms.push(event.data);
    }
  }

  /**
   * On confirm, returns the selected programs to the main component
   */
  onConfirm(): void {
    this.dialogRef.close({
      data: {
        programs: this.selectedPrograms
      }
    });
  }

  /**
   * On cancel, closes the pop up without returning anything
   */
  onCancel(): void {
    this.dialogRef.close();
  }
}
